import { required, email, numeric, helpers } from 'vuelidate/lib/validators'
import feedbackAPI from '@shared/api/shared/feedback'

const isNotEmailType = (value, vm) => vm.type !== 'email'
const isNotCheckboxType = (value, vm) => vm.type !== 'checkbox'
const isNotNumberType = (value, vm) => vm.type !== 'number'
const isTrue = (value) => value === true

export default {
  data: function () {
    return {
      // Demo Data Structure: { id: '462464', type: 'email', value: 'joe@somemail.com' }
      inputsToBeValidated: [],
    }
  },
  validations() {
    const customRequiredValidator = helpers.withParams(
      { type: 'customRequired' }, function (value, vm) {
        const index = this.inputsToBeValidated.findIndex(
          item => item.id === vm.id)
        const input = this.inputsToBeValidated[index]

        if (input && input.required) {
          if (!required(value)) {
            this.inputsToBeValidated[index].errorMessage = 'Field is required'
            return false
          }
          this.inputsToBeValidated[index].errorMessage = ''
          return true
        }

        return true
      })

    const customCheckboxValidator = helpers.withParams(
      { type: 'customCheckbox' }, function (value, vm) {
        if (isNotCheckboxType(value, vm)) {
          return true
        }
        const index = this.inputsToBeValidated.findIndex(
          item => item.id === vm.id)
        const input = this.inputsToBeValidated[index]

        if (input && input.required) {
          if (!isTrue(value)) {
            this.inputsToBeValidated[index].errorMessage = 'Checkbox must be checked'
            return false
          }
          return true
        }
        // Always return true if not required
        return true
      })

    const customNumberValidator = helpers.withParams({ type: 'customNumber' },
      function (value, vm) {
        if (isNotNumberType(value, vm)) {
          return true
        }

        if (!numeric(value)) {
          const index = this.inputsToBeValidated.findIndex(
            item => item.id === vm.id)
          if (index !== -1) {
            this.inputsToBeValidated[index].errorMessage = 'Enter a valid number'
          }
          return false
        }
        return true
      })

    const customEmailValidator = function (value, vm) {
      if (isNotEmailType(value, vm)) {
        return true
      }

      const index = this.inputsToBeValidated.findIndex(
        item => item.id === vm.id)

      if (!email(value)) {
        if (index !== -1) {
          this.inputsToBeValidated[index].errorMessage = 'Enter a valid email'
        }
        return false
      } else {
        // Clear error message if valid
        if (index !== -1) {
          this.inputsToBeValidated[index].errorMessage = ''
        }
      }

      if (vm.unique) {
        return asyncEmailValidator.call(this, value, vm)
      } else {
        return true
      }
    }

    const asyncEmailValidator = helpers.withParams({ type: 'asyncEmail' },
      function (value, vm) {
        if (!value) return Promise.resolve(true); // Skip validation if value is empty

        if (process.env.PROJECT === 'scriptGenerator') {
          return feedbackAPI
            .validateInput(this.backendURL,
              { email: value, widgetID: this.widget.id })
            .then((response) => {
              const index = this.inputsToBeValidated.findIndex(
                item => item.id === vm.id)
              if (response.data.valid) {
                if (index !== -1) {
                  this.inputsToBeValidated[index].errorMessage = ''
                }
                return true
              } else {
                if (index !== -1) {
                  this.inputsToBeValidated[index].errorMessage = response.data.error ||
                    'Invalid email'
                }
                return false
              }
            })
            .catch(() => {
              const index = this.inputsToBeValidated.findIndex(
                item => item.id === vm.id)
              if (index !== -1) {
                this.inputsToBeValidated[index].errorMessage = 'Server error'
              }
              return false
            })
        } else {
          return Promise.resolve(true)
        }
      })

    return {
      inputsToBeValidated: {
        $each: {
          value: {
            customRequired: customRequiredValidator,
            customNumber: customNumberValidator,
            customEmail: customEmailValidator,
            customCheckbox: customCheckboxValidator,
          },
        },
      },
    }
  },
  watch: {
    targetStepIndex() {
      this.updateInputsToBeValidated()
    },
  },
  mounted() {
    this.updateInputsToBeValidated()
  },
  methods: {
    updateInputsToBeValidated() {
      // Clear the existing inputs to prevent duplicates
      this.inputsToBeValidated = []

      const currentStepItems =
        this.widget.steps[this.targetStepIndex]?.contentItems || []

      const inputsThatNeedValidation = currentStepItems.filter(
        (item) => item.category === 'form',
      )

      inputsThatNeedValidation.forEach((contentItem, index) => {
        const contentItemType =
          contentItem.settings.type || contentItem.type

        const contentItemUnique =
          contentItemType === 'email' ? contentItem.settings.unique : false

        this.$set(this.inputsToBeValidated, index, {
          id: contentItem.id,
          type: contentItemType,
          required: contentItem.settings.required || false,
          unique: contentItemUnique,
          value: this.feedback.answer.items[contentItem.id]?.value || '',
          errorMessage: '',
        })
      })

      // Use $nextTick to ensure validations are available
      this.$nextTick(() => {
        this.$v.$reset()
        this.inputsToBeValidated.forEach((item, index) => {
          if (item.value) {
            this.$v.inputsToBeValidated.$each[index].$touch()
          }
        })
      })
    },
    getValidationIndex(componentID) {
      return this.inputsToBeValidated.findIndex(
        (item) => item.id === componentID,
      )
    },
    triggerUpdateValidationList(data) {
      const index = this.getValidationIndex(data.id)

      if (index !== -1) {
        this.inputsToBeValidated[index].value = data.value
        this.$v.inputsToBeValidated.$each[index].$touch()
      }
    },
  },
}